import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { Upload, AlertCircle, CheckCircle, Store, User, Phone, CreditCard, Camera, X } from "lucide-react";

function SellerForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    idCardNumber: "",
    currentAddress: "",
    registeredAddress: "",
    phoneNumber: "",
    email: "",
    bankAccountName: "",
    bankAccountNumber: "",
    bankName: "",
    idCardImage: null,
    selfieImage: null,
  });

  const [isVerifiedSeller, setIsVerifiedSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // ดึงค่า isVerifiedSeller จาก API
  useEffect(() => {
    const fetchVerificationStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("กรุณาเข้าสู่ระบบก่อน");
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/auth/check-verification`,
          { headers: { Authorization: token } }
        );

        if (!response.ok) throw new Error("Failed to fetch verification status");

        const data = await response.json();
        setIsVerifiedSeller(data.isVerifiedSeller);

        // ถ้าเป็นผู้ขายแล้ว (Status 2 = อนุมัติ) ไปหน้าจัดการร้านค้า
        if (data.isVerifiedSeller === 2) {
          navigate("/account/product-manager");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVerificationStatus();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      if (files[0].type.startsWith("image/") && files[0].size <= 5 * 1024 * 1024) {
        setFormData({ ...formData, [name]: files[0] });
      } else {
        alert("กรุณาอัปโหลดรูปภาพขนาดไม่เกิน 5MB");
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const formDataToSend = new FormData();

      Object.keys(formData).forEach((key) => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      console.log("Submitting seller form...");
      console.log("Token:", token);

      const response = await fetch(`${API_BASE_URL}/seller-verified`, {
        method: "POST",
        headers: { Authorization: token },
        body: formDataToSend,
      });

      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || "Failed to submit");
        } catch (parseError) {
          throw new Error(errorText || "Failed to submit");
        }
      }

      setSuccess(true);
    } catch (error) {
      console.error("Submit error:", error);
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">กำลังตรวจสอบสถานะ...</p>
        </div>
      </div>
    );
  }

  // Success State
  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-2xl p-12 text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ส่งข้อมูลสำเร็จ!</h2>
          <p className="text-gray-600 mb-8">ทีมงานจะตรวจสอบข้อมูลของคุณภายใน 1-3 วันทำการ</p>
          <button
            onClick={() => navigate("/home")}
            className="w-full py-3 px-6 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all"
          >
            กลับหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  // Pending Review State (Status 1)
  if (isVerifiedSeller === 1) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-sm p-12 text-center max-w-md">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">อยู่ระหว่างการตรวจสอบ</h2>
          <p className="text-gray-600 mb-4">คำขอของคุณกำลังอยู่ในขั้นตอนการพิจารณา</p>
          <p className="text-sm text-gray-500">สถานะ: รอการอนุมัติ (Pending)</p>
        </div>
      </div>
    );
  }

  // Rejected State (Status -1 = ถูกปฏิเสธ)
  if (isVerifiedSeller === -1) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-sm p-12 text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">คำขอถูกปฏิเสธ</h2>
          <p className="text-gray-600 mb-4">คำขอเปิดร้านค้าของคุณไม่ผ่านการอนุมัติ</p>
          <button
            onClick={() => setIsVerifiedSeller(0)} // Reset to allow resubmission
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            ยื่นเอกสารใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="p-4 bg-black rounded-full inline-block mb-4">
            <Store className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">สมัครเป็นผู้ขาย</h1>
          <p className="text-gray-500">กรอกข้อมูลเพื่อเริ่มขายสินค้ากับเรา</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-red-600" />
              ข้อมูลส่วนตัว
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อ</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">นามสกุล</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">เลขบัตรประชาชน</label>
                <input
                  type="text"
                  name="idCardNumber"
                  value={formData.idCardNumber}
                  onChange={handleChange}
                  required
                  maxLength={13}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Phone className="w-5 h-5 text-red-600" />
              ข้อมูลติดต่อ
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">เบอร์โทรศัพท์</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">อีเมล</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">ที่อยู่ปัจจุบัน</label>
                <textarea
                  name="currentAddress"
                  value={formData.currentAddress}
                  onChange={handleChange}
                  required
                  rows={2}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">ที่อยู่ตามบัตรประชาชน</label>
                <textarea
                  name="registeredAddress"
                  value={formData.registeredAddress}
                  onChange={handleChange}
                  required
                  rows={2}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none"
                />
              </div>
            </div>
          </div>

          {/* Bank Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-red-600" />
              ข้อมูลบัญชีธนาคาร
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ธนาคาร</label>
                <select
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                >
                  <option value="">เลือกธนาคาร</option>
                  <option value="กสิกรไทย">ธนาคารกสิกรไทย</option>
                  <option value="ไทยพาณิชย์">ธนาคารไทยพาณิชย์</option>
                  <option value="กรุงเทพ">ธนาคารกรุงเทพ</option>
                  <option value="กรุงไทย">ธนาคารกรุงไทย</option>
                  <option value="กรุงศรี">ธนาคารกรุงศรีอยุธยา</option>
                  <option value="ทหารไทยธนชาต">ธนาคารทหารไทยธนชาต</option>
                  <option value="ออมสิน">ธนาคารออมสิน</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อบัญชี</label>
                <input
                  type="text"
                  name="bankAccountName"
                  value={formData.bankAccountName}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">เลขบัญชี</label>
                <input
                  type="text"
                  name="bankAccountNumber"
                  value={formData.bankAccountNumber}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          {/* Document Upload */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Camera className="w-5 h-5 text-red-600" />
              เอกสารยืนยันตัวตน
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ID Card Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">รูปบัตรประชาชน</label>
                <div className="relative">
                  {formData.idCardImage ? (
                    <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={URL.createObjectURL(formData.idCardImage)}
                        alt="ID Card"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, idCardImage: null })}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">อัปโหลดรูปภาพ</span>
                      <input
                        type="file"
                        name="idCardImage"
                        accept="image/*"
                        onChange={handleChange}
                        className="hidden"
                        required
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Selfie Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">รูปถ่ายคู่บัตรประชาชน</label>
                <div className="relative">
                  {formData.selfieImage ? (
                    <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={URL.createObjectURL(formData.selfieImage)}
                        alt="Selfie"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, selfieImage: null })}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">อัปโหลดรูปภาพ</span>
                      <input
                        type="file"
                        name="selfieImage"
                        accept="image/*"
                        onChange={handleChange}
                        className="hidden"
                        required
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 px-8 bg-red-600 text-white rounded-lg font-semibold text-lg hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Store size={20} />
                ส่งคำขอ
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SellerForm;
